import { useLocalStorage } from "@medoma/shared-employee/useLocalStorage";
import { useUserProfile } from "@/api/Users";
import { useCallback, useMemo } from "react";
import type { z } from "zod";

// Multi-select filter backed by per-user localStorage — a filter inherited from
// the previous employee on a shared computer would hide rows while still
// reading as the whole set. Default state is empty — no ids selected reads as
// "no filter applied" and shows everything. Any other selection writes those
// exact ids, including when every enabled id is selected — the boxes need to
// stay checked in the UI, and `allSelected` already treats a full set the same
// as an empty one for filtering.
export const useSelectedFilter = <T extends string | number>({
  storageKey,
  schema,
  enabledIds,
}: {
  storageKey: string;
  schema: z.ZodType<T[]>;
  enabledIds: readonly T[] | undefined;
}) => {
  const { userId } = useUserProfile();
  const [rawStored, setRawStored] = useLocalStorage({
    key: storageKey,
    userId,
  });

  const storedSelection = useMemo(() => {
    if (!rawStored) {
      return null;
    }
    try {
      return new Set(schema.parse(JSON.parse(rawStored)));
    } catch {
      return null;
    }
  }, [rawStored, schema]);

  const selected = useMemo(() => {
    if (!enabledIds || !storedSelection) {
      return new Set<T>();
    }
    return new Set(enabledIds.filter((id) => storedSelection.has(id)));
  }, [enabledIds, storedSelection]);

  const allSelected =
    !enabledIds || selected.size === 0 || selected.size === enabledIds.length;

  const setSelected = useCallback(
    (next: Set<T>) => {
      if (!enabledIds) {
        return;
      }
      setRawStored(JSON.stringify([...next]));
    },
    [enabledIds, setRawStored],
  );

  const toggle = useCallback(
    (id: T, on: boolean) => {
      const next = new Set(selected);
      if (on) {
        next.add(id);
      } else {
        next.delete(id);
      }
      setSelected(next);
    },
    [selected, setSelected],
  );

  const clear = useCallback(() => setRawStored(""), [setRawStored]);

  return { selected, allSelected, setSelected, toggle, clear };
};
